import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import './Login.css';

const Login = () => {
  const [formState, setFormState] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  // Using environment variables for Cognito configuration
  const cognitoDomain = process.env.REACT_APP_COGNITO_DOMAIN;
  const clientId = process.env.REACT_APP_COGNITO_CLIENT_ID;
  const redirectUri = process.env.REACT_APP_COGNITO_REDIRECT_URI || window.location.origin + '/login';

  // Debug environment variables
  console.log('Cognito Domain:', cognitoDomain);
  console.log('Client ID:', clientId);
  console.log('Redirect URI:', redirectUri);

  useEffect(() => {
    // Check if we're returning from Cognito with a code parameter
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');

    if (code) {
      // Handle the authorization code
      // In a real implementation, you would exchange this code for tokens
      console.log('Authorization code received:', code);
      navigate('/dashboard');
    }
  }, [navigate]);

  const handleChange = (e) => {
    setFormState({ ...formState, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // For local development, we can hardcode the values if needed
      const domain = cognitoDomain || 'us-east-1egnwrgpsl.auth.us-east-1.amazoncognito.com';
      const id = clientId || '6qbsc7ujekqp6s90q31aao7hp7';
      const redirect = redirectUri || window.location.origin + '/dashboard';

      const encodedRedirectUri = encodeURIComponent(redirect);
      window.location.href = `https://${domain}/login?client_id=${id}&response_type=code&scope=email+openid+profile&redirect_uri=${encodedRedirectUri}`;
    } catch (err) {
      console.error('Error during login:', err);
      setError(err.message || 'Failed to sign in. Please check your credentials.');
      setLoading(false);
    }
  };

  // Option to use direct API integration instead of redirection
  const handleDirectLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Here you would use AWS Amplify or fetch directly
      // Example with fetch:
      const response = await fetch('/api/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formState)
      });

      const data = await response.json();

      if (data.success) {
        // Store tokens and redirect
        localStorage.setItem('idToken', data.idToken);
        navigate('/dashboard');
      } else {
        setError(data.message || 'Login failed');
      }
    } catch (err) {
      setError('Login service unavailable. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <Navbar />
      <div className="login-container">
        <div className="login-form-wrapper">
          <h1>Welcome to Pixelate</h1>
          <p className="login-subtitle">Sign in to continue to your dashboard</p>

          {error && <div className="error-message">{error}</div>}

          <form onSubmit={handleSubmit} className="login-form">
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                id="email"
                type="email"
                name="email"
                placeholder="Enter your email"
                value={formState.email}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input
                id="password"
                type="password"
                name="password"
                placeholder="Enter your password"
                value={formState.password}
                onChange={handleChange}
                required
              />
            </div>

            <div className="forgot-password">
              <a href={`https://${cognitoDomain || 'us-east-1egnwrgpsl.auth.us-east-1.amazoncognito.com'}/forgotPassword?client_id=${clientId || '6qbsc7ujekqp6s90q31aao7hp7'}&redirect_uri=${encodeURIComponent(redirectUri || window.location.origin + '/dashboard')}`}>
                Forgot password?
              </a>
            </div>

            <button
              type="submit"
              className="login-button"
              disabled={loading}
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </button>

            {/* Optional: Direct API integration button 
            <button 
              onClick={handleDirectLogin} 
              className="direct-login-button"
              disabled={loading}
            >
              Sign In Directly
            </button>
            */}
          </form>

          <div className="signup-prompt">
            <p>
              Don't have an account?{' '}
              <a href={`https://${cognitoDomain || 'us-east-1egnwrgpsl.auth.us-east-1.amazoncognito.com'}/oauth2/authorize?client_id=${clientId || '6qbsc7ujekqp6s90q31aao7hp7'}&response_type=code&scope=email+openid+profile&redirect_uri=${encodeURIComponent(redirectUri || window.location.origin + '/dashboard')}&identity_provider=COGNITO`}>
                Sign up
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login; 